import { useState } from 'react';
import { addProfileToBusiness } from '../../services/businessService';

interface AddClientAdminModalProps {
  businessId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddClientAdminModal({ businessId, onClose, onSuccess }: AddClientAdminModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !displayName.trim()) return;

    setError('');
    setLoading(true);
    try {
      await addProfileToBusiness({
        email: email.trim(),
        displayName: displayName.trim(),
        businessId,
        persona: 'business',
        role: 'client_admin',
        delayNotification: false,
      });
      onSuccess();
    } catch {
      setError('Failed to add client admin');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const emailInvalid = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Add Client Admin</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`${inputClass}${emailInvalid ? ' border-red-400' : ''}`}
                  placeholder="email@example.com"
                />
                {emailInvalid && (
                  <p className="text-red-500 text-xs mt-1">Enter a valid email (e.g. user@example.com)</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Display Name *</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Display name"
                />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim() || !displayName.trim() || emailInvalid}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Adding...' : 'Add Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
